import React, { useState, useEffect, useCallback } from 'react';
import api from './api';

// L'écran ne montre pas « les prix » — ça, les sites le font mieux. Il montre
// CE QUI A BOUGÉ depuis le relevé précédent : un relevé permanent ne vaut que
// par la comparaison.

const montant = (n, devise) =>
  n == null ? '—' : Number(n).toLocaleString('fr-FR', { style: 'currency', currency: devise || 'EUR' });
const dateHeure = (d) =>
  d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

// Variation en valeur ET en pourcentage : 1 € de hausse ne dit pas la même chose
// sur une bouteille à 8 € et sur un coffret à 590 €.
function Variation({ courant, precedent, devise }) {
  if (courant == null || precedent == null) return <span className="neutre">—</span>;
  const d = Number(courant) - Number(precedent);
  if (Math.abs(d) < 0.005) return <span className="neutre">=</span>;
  const pct = precedent ? (d / Number(precedent)) * 100 : 0;
  return (
    <span className={d > 0 ? 'hausse' : 'baisse'} title={`Précédent : ${montant(precedent, devise)}`}>
      {d > 0 ? '▲' : '▼'} {montant(Math.abs(d), devise)} ({Math.abs(pct).toFixed(1)} %)
    </span>
  );
}

export default function Veille({ utilisateur }) {
  const estAdmin = utilisateur?.role === 'admin';
  const [sites, setSites] = useState([]);
  const [sources, setSources] = useState([]);
  const [sourceId, setSourceId] = useState(null);
  const [releve, setReleve] = useState(null);
  const [charge, setCharge] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState('');
  const [recherche, setRecherche] = useState('');
  const [mouvements, setMouvements] = useState(false);
  const [modale, setModale] = useState(false);
  const [form, setForm] = useState({ libelle: '', site: 'vinatis', url: '', mots_cles: '' });

  const chargerSources = useCallback(async () => {
    try {
      const { data } = await api.get('/veille/sources');
      setSources(data.sources);
      setSourceId((p) => p || data.sources[0]?.id || null);
    } catch { setErreur('Impossible de charger les sources.'); }
    finally { setCharge(false); }
  }, []);

  useEffect(() => { chargerSources(); api.get('/veille/sites').then((r) => setSites(r.data.sites)).catch(() => {}); }, [chargerSources]);

  const chargerReleve = useCallback(async () => {
    if (!sourceId) { setReleve(null); return; }
    try { setReleve((await api.get(`/veille/releves/${sourceId}`)).data); } catch { setReleve(null); }
  }, [sourceId]);
  useEffect(() => { chargerReleve(); }, [chargerReleve]);

  const relever = async (id) => {
    setEnCours(true); setErreur('');
    try {
      const { data } = await api.post(`/veille/relever${id ? '/' + id : ''}`);
      const ko = (data.resultats || []).filter((r) => r.erreur);
      if (ko.length) setErreur(ko.map((r) => `${r.source} : ${r.erreur}`).join(' — '));
      await chargerSources(); await chargerReleve();
    } catch (e) {
      setErreur(e.response?.data?.resultats?.map((r) => `${r.source} : ${r.erreur}`).join(' — ')
        || e.response?.data?.error || 'Le relevé a échoué.');
    } finally { setEnCours(false); }
  };

  const ajouterSource = async () => {
    try {
      await api.post('/veille/sources', form);
      setModale(false); setForm({ libelle: '', site: 'vinatis', url: '', mots_cles: '' });
      chargerSources();
    } catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };

  const supprimerSource = async (s) => {
    if (!window.confirm(`Supprimer « ${s.libelle} » ?\n\nTout son historique de relevés part avec elle.`)) return;
    try { await api.delete(`/veille/sources/${s.id}`); setSourceId(null); chargerSources(); }
    catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };

  const source = sources.find((s) => s.id === sourceId);
  const devise = source?.devise || 'EUR';
  const lignes = (releve?.lignes || []).filter((l) => {
    if (recherche && !`${l.nom} ${l.appellation || ''}`.toLowerCase().includes(recherche.toLowerCase())) return false;
    if (mouvements) {
      const bouge = l.prix_precedent != null && Math.abs(Number(l.prix) - Number(l.prix_precedent)) >= 0.005;
      return bouge || l.nouveau;
    }
    return true;
  });

  if (charge) return <p className="attente">Chargement…</p>;

  if (sources.length === 0) {
    return (
      <div className="vide">
        <div className="vide-icone">🍷</div>
        <p>Aucune source suivie.</p>
        {estAdmin && <button onClick={() => setModale(true)}>+ Ajouter une source</button>}
        {modale && <Modale {...{ form, setForm, sites, ajouterSource, fermer: () => setModale(false) }} />}
      </div>
    );
  }

  return (
    <>
      <div className="barre">
        <input className="recherche" placeholder="🔍 Rechercher une référence…" value={recherche} onChange={(e) => setRecherche(e.target.value)} />
        <select value={sourceId || ''} onChange={(e) => setSourceId(parseInt(e.target.value, 10))}>
          {sources.map((s) => <option key={s.id} value={s.id}>{s.libelle}{s.actif ? '' : ' (inactive)'}</option>)}
        </select>
        <label className="bascule">
          <input type="checkbox" checked={mouvements} onChange={(e) => setMouvements(e.target.checked)} /> Mouvements uniquement
        </label>
        {estAdmin && <button onClick={() => relever(sourceId)} disabled={enCours}>{enCours ? '⏳ Relevé…' : '🔄 Relever'}</button>}
        {estAdmin && <button onClick={() => relever(null)} disabled={enCours}>Tout relever</button>}
        <a className="bouton" href={`/api/veille/export/${sourceId}`}>⬇️ CSV</a>
        {estAdmin && <button onClick={() => setModale(true)}>+ Source</button>}
        {estAdmin && source && <button className="danger" onClick={() => supprimerSource(source)}>🗑️</button>}
      </div>

      {erreur && (
        <div className="alerte">
          <strong>Relevé refusé — rien n'a été enregistré.</strong>
          <div>{erreur}</div>
          <div className="discret">Le garde-fou empêche d'écraser l'historique par un relevé vide ou incomplet.</div>
        </div>
      )}

      <div className="entete">
        <span>Relevé du <strong>{dateHeure(releve?.releve_le)}</strong></span>
        <span className="discret">{releve?.precedent_le ? `comparé au ${dateHeure(releve.precedent_le)}` : 'premier relevé — pas encore de comparaison'}</span>
        <span className="discret">{lignes.length} référence{lignes.length > 1 ? 's' : ''} · {devise}</span>
      </div>

      <div className="tableau">
        <table>
          <thead>
            <tr>
              <th>Référence</th><th>Appellation</th><th>Conditionnement</th>
              <th className="droite">Prix</th><th>Variation</th>
              <th className="droite" title="Prix ramené à la bouteille, ou meilleur prix dégressif">Par bouteille</th>
              <th className="droite">Stock</th>
            </tr>
          </thead>
          <tbody>
            {lignes.length === 0 && (
              <tr><td colSpan={7} className="discret centre">
                {mouvements ? 'Aucun mouvement depuis le relevé précédent.' : 'Aucune référence sur ce relevé.'}
              </td></tr>
            )}
            {lignes.map((l) => (
              <tr key={l.id}>
                <td>
                  <a href={l.url} target="_blank" rel="noopener noreferrer">{l.nom}</a>
                  {l.nouveau && <span className="pastille">nouveau</span>}
                </td>
                <td className="discret">{l.appellation || l.region || '—'}{l.millesime ? ` ${l.millesime}` : ''}</td>
                <td className="discret">{l.conditionnement || '—'}</td>
                <td className="droite nombre">{montant(l.prix, l.devise || devise)}</td>
                <td><Variation courant={l.prix} precedent={l.prix_precedent} devise={l.devise || devise} /></td>
                <td className="droite nombre">
                  {montant(l.prix_degressif, l.devise || devise)}
                  {l.seuil_degressif > 1 && <span className="discret seuil">×{l.seuil_degressif}</span>}
                </td>
                <td className="droite nombre">{l.stock != null ? l.stock : (l.stock_libelle || '—')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Une référence qui disparaît du catalogue est une information
          commerciale — elle ne doit pas s'évaporer en silence. */}
      {releve?.disparues?.length > 0 && (
        <div className="disparues">
          <strong>{releve.disparues.length} référence(s) retirée(s) depuis le relevé précédent :</strong>
          <ul>{releve.disparues.map((d) => <li key={d.ref_externe}>{d.nom} <span className="discret">({montant(d.prix, devise)})</span></li>)}</ul>
        </div>
      )}

      {modale && <Modale {...{ form, setForm, sites, ajouterSource, fermer: () => setModale(false) }} />}
    </>
  );
}

function Modale({ form, setForm, sites, ajouterSource, fermer }) {
  const site = sites.find((s) => s.cle === form.site);
  return (
    <div className="voile" onClick={fermer}>
      <div className="modale" onClick={(e) => e.stopPropagation()}>
        <h2>Nouvelle source</h2>
        <label>Libellé</label>
        <input value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })} placeholder="Xavier Vignon — Vinatis" />
        <label>Site</label>
        <select value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value, url: '' })}>
          {sites.map((s) => <option key={s.cle} value={s.cle}>{s.libelle} ({s.devise})</option>)}
        </select>
        <label>URL{site?.defautUrl ? ' (facultative)' : ''}</label>
        <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder={site?.defautUrl || ''} />
        {site && <p className="aide">{site.aide}</p>}
        <label>Marque suivie</label>
        <input value={form.mots_cles} onChange={(e) => setForm({ ...form, mots_cles: e.target.value })} placeholder="xavier vignon" />
        <p className="aide">
          Sépare plusieurs mots-clés par une virgule. Obligatoire : une page producteur
          n'est jamais pure, elle mêle des suggestions d'autres domaines.
        </p>
        <div className="actions">
          <button className="lien" onClick={fermer}>Annuler</button>
          <button onClick={ajouterSource} disabled={!form.libelle || !form.mots_cles}>Enregistrer</button>
        </div>
      </div>
    </div>
  );
}
